// const Cookie = require('js-cookie');
import ErrorMessage from '../components/ErrorMessage'
import {navigate, refresh} from '../kb'
import download from './download'
import type { NotifyMessageType} from '../notify';
import notify, {notifyError, notifyMessage, notifyWarning} from '../notify'
import _ from 'lodash'
import type {Jsonable, JsonRequest, JsonResponse} from './jsonSerializable';
import { jsonStringify} from './jsonSerializable'
import Router from '../Router'
import {isDangerousHtml} from './react-helpers'
import {Warning} from './errors'
import {captureSentryError, logErrorMessage} from '../sentry'
import type {ErrorCode} from './ErrorCode'
import type {Resolvable} from '../util/resolvable';
import { resolveValue} from '../util/resolvable'

interface StandardErrorData {
    error?: NotifyMessageType
    errors?: string|string[]
    message?: string
    code?: ErrorCode
}

interface StandardErrorResponse {
    status?: number
    data?: StandardErrorData
}

interface StandardResponseData extends StandardErrorData {
    redirect?: string
    location?: string
    success?: NotifyMessageType
}

// export type ResponseWithData<T={}> = Response & { data: Jsonable<T> & StandardResponse }
// export type ResponseWithData<TSuccess=StandardResponseData,TError=StandardErrorData> = Override<Response, XOR<{ok: true, data: TSuccess}|{ok: false, data: TError}>>
export type ResponseWithData<TSuccess=StandardResponseData,TError=StandardErrorData> = Response & ({ok: true, data: TSuccess}|{ok: false, data: TError})
export type SuccessResponse<TSuccess=StandardResponseData> = Override<Response, {ok: true, data: TSuccess}>
export type ErrorResponse<TError=StandardErrorData> = Override<Response, {ok: false, data: TError}>
export type PromiseOfResponseWithData<TSuccess=StandardResponseData,TError=StandardErrorData> = Promise<ResponseWithData<TSuccess,TError>>
export type QueryParams = Record<string,UrlParam|UrlParam[]>
export type RequestOptions = RequestInit & {queryParams?: QueryParams}

const noop = () => {/*do nothing*/}

export function getLaravelCsrfToken() {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
}

export function appendQueryParams(url: string, params?: QueryParams) {
    if(params) {
        url += (url.includes('?') ? '&' : '?') + encodeQueryParams(params);
    }
    return url
}

export function uploadBlob(url: string, blob: Blob, headers: Record<string,string|number>) {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url);
    xhr.setRequestHeader('X-CSRF-TOKEN', getLaravelCsrfToken()!)
    // xhr.setRequestHeader('X-Size', String(blob.size))

    for(const [key,value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, String(value))
    }

    setImmediate(() => {
        xhr.send(blob)
    })

    return xhr
}

function fetchWithCsrf(url: string, options: RequestOptions = {}): Promise<Response> {
    if (!url) {
        return Promise.reject(new Error("Missing URL"))
    }

    // https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch
    options = {
        credentials: 'same-origin',
        redirect: 'manual', // fetch() is stupid: https://stackoverflow.com/q/42716082/65387
        ...options,
    };

    const csrfToken = getLaravelCsrfToken();

    if (csrfToken) {
        _.set(options, ['headers', 'X-CSRF-TOKEN'], csrfToken);
    }

    if (options.queryParams) {
        url = appendQueryParams(url, options.queryParams);
        delete options.queryParams;
    }

    const method = options.method || 'GET'

    // convert fetch() result to polyfilled Promise
    return fetch(url, options).then(res => {
        if(res.type === 'opaqueredirect') {
            throw new Error(`Unexpected redirect in response to ${method} ${res.url || url}`)
        } else if(res.status === 0) {
            throw new Error(`Unexpected 0 status in response to ${method} ${res.url || url}`)
        }
        return res;
    }, err => {
        throw new Error(`Failed to fetch: ${method} ${url}`, {cause: err})
    })
}

export type UrlParam = string | number | boolean | undefined | null

export function encodeParam(x: UrlParam): string {
    if (x === true) {
        return '1';
    }
    if (x === false) {
        return '0';
    }
    if (x === undefined || x === null) {
        return '';
    }
    return encodeURIComponent(x);
}

export function encodeQueryParams(params: QueryParams): string {
    return Object.keys(params)
        .filter(k => params[k] !== undefined)
        .map(k => {
            const p = params[k]
            if (Array.isArray(p)) {
                return p
                    .map(val => `${encodeParam(k)}[]=${encodeParam(val)}`)
                    .join('&');
            }
            return `${encodeParam(k)}=${encodeParam(p)}`
        })
        .join('&');
}

export function ajax<T>(url: string, options: RequestOptions = {}): PromiseOfResponseWithData<T> {
    // @ts-expect-error 'T' could be instantiated with an arbitrary type which could be unrelated to 'unknown'.
    return fetchWithCsrf<T>(url, options).then(res => {
        const contentType = res?.headers?.get('Content-Type');

        if (contentType) {
            if (/^application\/json($|;)/i.test(contentType)) {
                return res.json().then(setResponseData(res))
            }

            if (/^(multipart\/form-data|application\/x-www-form-urlencoded)($|;)/i.test(contentType)) { // TODO: check if .formData supports both
                return res.formData().then(setResponseData(res))
            }

            if (/^text\//i.test(contentType)) {
                return res.text().then(setResponseData(res));
            }
        }

        return setResponseData(res)(Object.create(null))
    }).then(res => {
        if(!res.ok) {
            if(res.status < 400 || res.status >= 500) {
                logErrorMessage(`${options.method || 'GET'} "${url}" returned ${res.status}`, {
                    extra: {
                        function: 'fetchWithCsrf',
                        message: responseToErrorMessage(res),
                        data: res.data,
                        url,
                        method: options.method,
                        status: res.status,
                    }
                });
            }
        }
        return res;
    }, err => {
        captureSentryError("Ajax network error", err, {
            function: 'fetchWithCsrf',
            message: "Network error",  // TODO: is there any other data I can get out of this error?
            url: url,
            method: options.method,
            requestHeaders: options.headers,
        }, '111bd8b1-2036-41ff-a61c-f0a23b2bde56')
        throw err
    })
}


interface PostOptions<T> {
    url: string
    data?: Jsonable<T>
    filename: string
    contentType?: string
}

export function postDownload<T>({
                                 url,
                                 data,
                                 filename,
                                 contentType
                             }: PostOptions<T>) {
    return fetchWithCsrf(url, {
        method: 'POST',
        headers: {
            'Accept': '*/*',
            'Content-Type': 'application/json',
        },
        body: data !== undefined ? jsonStringify(data) : undefined,
    }).then(res => {
        if(res.ok) {
            return Promise.all([res.blob(), res.headers.get('x-filename'), res.headers.get('content-type')])
                .then(([blob, hFilename, hContentType]) => {
                    download(blob, hFilename || filename, hContentType || contentType);
                })
        }
        throw new Error(`${res.status} ${res.statusText}`)
    });
}

function setResponseData<T>(res: Response): (data: T) => ResponseWithData<T> {
    return function (data: T) {
        (res as ResponseWithData<T>).data = data;
        return res as ResponseWithData<T>
    };
}

// function checkServerError(response) {
//     if (response.status >= 200 && response.status < 300 || response.status >= 400 && response.status < 500) {
//         return response;
//     }
//     let error = new Error(response.statusText);
//     error.response = response;
//     throw error;
// }



// function jsonStringify2(obj: any): string {
//     if(Array.isArray(obj)) {
//         return '[' + obj.map(jsonStringify).join(',') + ']'
//     }
//     if(obj instanceof Set) {
//         return jsonStringify(Array.from(obj))
//     }
//     if(obj instanceof Map) {
//         return jsonStringify(Object.fromEntries(obj.entries()))
//     }
//     if(obj instanceof Date) {
//         return JSON.stringify(obj.toISOString())
//     }
//     if(_.isPlainObject(obj)) {
//         return '{'+Object.keys(obj).map(k => `${JSON.stringify(k)}:${jsonStringify(obj[k])}`).join(',')+'}'
//     }
//     return JSON.stringify(obj)
// }


/**
 * POSTs JSON to server, and expects JSON back.
 * Does not check if the response is OK.
 */
export function postJson<TResData,TReqData extends JsonRequest=JsonRequest,TError=StandardErrorData>(url: string, data?: TReqData, options: RequestOptions = {}): PromiseOfResponseWithData<TResData,TError> {
    const reqOpts: RequestOptions = {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        ...options,
    }
    if(data !== undefined) {
        reqOpts.body = jsonStringify(data)
    }
    if(typeof Sentry !== 'undefined') {
        Sentry.addBreadcrumb({
            category: 'postJson',
            message: `${url} ${reqOpts.body}`,
            level: Sentry.Severity.Info,
        })
    }
    return ajax(url, reqOpts);
}

export function postJsonOk<TResData,TReqData extends JsonRequest=JsonRequest>(route: RouteParams, data: TReqData): Promise<TResData> {
    return postJson<TResData,TReqData>(resolveRoute(route), data)
        .then(res => {
            if(res.ok) {
                return res.data
            }
            throw res
        })
}

export function deleteJson<TResponse,TRequest>(url: string, data?: Jsonable<TRequest>, options: Omit<RequestOptions,'method'> = {}): PromiseOfResponseWithData<TResponse> {
    return postJson(url, data, {...options, method: 'DELETE'})
}

export function getJsonOrNotify<TResData>(route: RouteParams, data: QueryParams): Promise<TResData> {
    return getJson<TResData>(resolveRoute(route), data)
        .then(res => {
            if(res.ok) {
                return res.data
            }
            notifyStandardError(res)
            throw res
        }, err => {
            notifyNetworkError(err)
            throw err;
        })
}

/**
 * Encodes data as query params, and expects JSON back.
 */
export function getJson<TResponse>(url: string, data?: QueryParams, options?: RequestInit): PromiseOfResponseWithData<TResponse> {
    if(typeof Sentry !== 'undefined') {
        Sentry.addBreadcrumb({
            category: 'getJson',
            message: appendQueryParams(url, data),
            level: Sentry.Severity.Info,
        })
    }
    return ajax(url, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
        },
        queryParams: data,
        ...options,
    });
}

interface NotifyStandardErrorOptions<TResData=any> {
    fallbackText?: Resolvable<NotifyMessageType, [ErrorResponse<TResData>]>
}

export function responseToErrorMessage(res: ResponseWithData<StandardErrorData|unknown>): string {
    const data = (res?.data || {}) as StandardErrorData

    if (data.error) {
        if(isDangerousHtml(data.error)) {
            return data.error.__html;
        }
        return data.error
    }

    if (data.errors?.length) {
        if(Array.isArray(data.errors)) {
            if(data.errors.length === 1) {
                return data.errors[0]
            }
            return data.errors.join('; ')
        }
        return data.errors
    }

    return `Error ${res.status}`
}

export function responseToError(res: ResponseWithData<any>): Error {
    return new Error(responseToErrorMessage(res))
}

export function notifyStandardError(res: ResponseWithData, opts: NotifyStandardErrorOptions = {}): void {
    const data = res?.data || {}
    let resolvedText: NotifyMessageType | undefined

    if (data.error) {
        notifyMessage('error',data.error)
    } else if (data.errors) {
        notifyWarning(ErrorMessage({errors: data.errors}))
    } else if (opts.fallbackText && (resolvedText = resolveValue(opts.fallbackText, res))) {
        notifyMessage('error',resolvedText)
    } else if (res?.status == 401) {
        notifyError(`You must log in to do that.`)
    } else if (res?.status == 403) {
        notifyError(`You're not allowed to do that.`)
    } else if (res?.status == 409) {
        notifyError(`There was a conflict. Perhaps this record already exists or it overlaps with something else.`)
    } else if (res?.status == 404) {
        notifyError(`We couldn't find what you were looking for.`)
    } else if (res?.status == 429) {
        notifyError(`You're doing that too much. Please try again later!`)
    } else if (res?.status == 501) {
        notifyError(`You've found an incomplete feature. Perhaps we're still working on it.`)
    } else if (res.status && res.status >= 400 && res.status < 500) {
        notifyError(`There was an error in your submission, but we're not sure what's wrong with it.`)
    } else if (res.status && res.status >= 500 && res.status < 600) {
        notifyError("A server error occurred. Your submission may not have been saved, was partially saved, or maybe it was saved but then something bad happened. Please bear with us while we get this sorted out!")
    } else if (res.status && res.status >= 300 && res.status < 400) {
        notifyError(`The server is trying to redirect you somewhere, but that's not supposed to happen!`)
    } else {
        notifyError(`An unknown error occurred.`)
    }

    // throw new Warning(responseToErrorMessage(res));
    // throw new Error("Error response")
}

function submitSuccessRedirectHandler<TResponse>(res: ResponseWithData<TResponse>) {
    const data: StandardResponseData = res.data || {};

    if (res.status >= 200 && res.status < 300) {
        const redirectUrl = data.redirect || data.location || res.headers.get('location');

        if (redirectUrl) {
            if (__DEV__) console.info(`Redirecting to ${redirectUrl}`)
            navigate(redirectUrl);
            return;
        }

        if (__DEV__) console.warn('Response was OK but `data.redirect` is missing. Response:', res, 'Data:', data);

        // notify({
        //     className: 'warning',
        //     text: `Submission was successful, but we're not sure where it went.`,
        // });

        refresh()
        return;
    }

    if (__DEV__) console.warn(`Bad response (${res.status}):`, res, 'Data:', data);

    notifyStandardError(res);
    throw new Warning(responseToErrorMessage(res))
}

export function notifyNetworkError(err: any): never {
    if (__DEV__) console.error(err);
    notify({
        html: "A network error occurred. Please try a few more times!",
        className: "error",
    });
    throw err; // propagate the error
}

/**
 * POST some JSON to the server.
 * The server should response with {"redirect":"/url"} or a "Location: /url" header.
 * It should also "flash" a message to the session storage to inform the user of the success *after* the redirect.
 */
export function submitServerRedirect<T>(url: string, data?: JsonRequest|Jsonable<T>) {
    if (__DEV__) console.info(`Submitting to ${url}. Data:`, data);

    return postJson(url, data).then(submitSuccessRedirectHandler, notifyNetworkError);
}

export function stdDelete<T>(url: string, data?: Jsonable<T>) {
    if (__DEV__) console.info(`DELETE ${url}. Data:`, data);

    return deleteJson(url, data).then(submitSuccessRedirectHandler, notifyNetworkError);
}

export type RouteGetParams = Record<string,string|number>
export type RouteArrayParams = [route: string, params: RouteGetParams]
export type RouteParams = string | RouteArrayParams

export interface PostJsonStdErrorOptions {
    url?: string
    route?: RouteParams
    data?: Resolvable<JsonRequest>
    fallbackError?: NotifyMessageType
    fallbackSuccess?: NotifyMessageType
}

export function resolveRoute(combined: RouteParams): string;
export function resolveRoute(route: string, params?: RouteGetParams): string;
export function resolveRoute(route: RouteParams, params?: RouteGetParams): string {
    let url: string|undefined;
    if(params !== undefined) {
        if(Array.isArray(route)) {
            throw new Error("Cannot supply array route and separate params")
        }
        url = Router.route(route,params)
    } else if(Array.isArray(route)) {
        url = Router.route(...route)
    } else {
        url = Router.route(route)
    }
    if(!url) {
        // if(__DEV__) console.error("Could not resolve route",route)
        throw new Error("Could not resolve route "+jsonStringify(route));
    }
    // if(__DEV__) console.info("Resolved route",route,"->",url)
    return url
}

export function postJsonStd<TResData=JsonResponse>(opts: PostJsonStdErrorOptions): Promise<TResData> {
    if(!opts.url && !opts.route) throw new Error("Must set `url` or `route`");
    const url = opts.url ?? resolveRoute(opts.route!)
    const data = resolveValue(opts.data)

    return postJson<TResData&{success?:string}>(url, data).then(res => {
        if(res.ok) {
            if(res.data?.success) {
                notifyMessage('success', res.data.success);
            } else if(opts.fallbackSuccess) {
                notifyMessage('success', opts.fallbackSuccess);
            }
            return res.data;
        } else {
            notifyStandardError(res, {fallbackText: opts.fallbackError})
            throw new Warning(responseToErrorMessage(res))
        }
    }, notifyNetworkError)
}
